import { brandUrls, logoHeight, type BrandUrls } from "../brand.ts";
import { cx, h, type Child } from "./dom.ts";
import { icon, type IconNode } from "./icons.ts";

export type BadgeTone = "neutral" | "accent" | "solid" | "success" | "warning" | "destructive";

/** Selo de situação (texto, nunca só cor). */
export const badge = (text: string, tone: BadgeTone = "neutral", node?: IconNode): HTMLSpanElement =>
  h("span", { class: cx("badge", tone !== "neutral" && `badge-${tone}`) }, node ? icon(node) : null, text);

export interface CardOptions {
  title?: string | undefined;
  /** Nível do título (padrão h3: o cartão fica dentro de uma seção). */
  level?: 2 | 3 | 4 | undefined;
  description?: string | undefined;
  footer?: Child[] | undefined;
  class?: string | undefined;
}

/** Superfície com borda: título, descrição, conteúdo e rodapé de ações. */
export function card(options: CardOptions, ...children: Child[]): HTMLElement {
  const title = options.title ? h(`h${options.level ?? 3}` as "h3", { class: "card-title" }, options.title) : null;
  return h(
    "section",
    { class: cx("card", options.class) },
    title || options.description ? h("div", { class: "card-header" }, title, options.description ? h("p", { class: "card-description" }, options.description) : null) : null,
    ...children,
    options.footer?.length ? h("div", { class: "card-footer" }, ...options.footer) : null,
  );
}

/** Tecla ou combinação (`kbd("Ctrl", "Z")`). */
export const kbd = (...keys: string[]): HTMLElement =>
  keys.length === 1 ? h("kbd", { class: "kbd" }, keys[0]) : h("span", { class: "inline-flex items-center gap-1" }, ...keys.map((k) => h("kbd", { class: "kbd" }, k)));

export const separator = (vertical = false): HTMLDivElement => h("div", { role: "separator", "aria-orientation": vertical ? "vertical" : undefined, class: cx("separator", vertical && "separator-vertical") });

export interface LogoOptions {
  /** Largura exibida em px (o `srcset` escolhe o arquivo). */
  width: number;
  /** `sizes` do `srcset`; o padrão é a própria largura. */
  sizes?: string | undefined;
  alt?: string | undefined;
  /** A logo do topo da página: sem `lazy` e com prioridade alta (é o maior elemento da primeira tela). */
  priority?: boolean | undefined;
  class?: string | undefined;
  urls?: BrandUrls | undefined;
}

/** A logo do Eldrun (AVIF e WebP com `srcset`): a única imagem de interface do sistema (ADR 0024). */
export function logo(options: LogoOptions): HTMLPictureElement {
  const brand = options.urls ?? brandUrls();
  const sizes = options.sizes ?? `${options.width}px`;
  return h(
    "picture",
    {},
    h("source", { type: "image/avif", srcset: brand.logo.avif, sizes }),
    h("source", { type: "image/webp", srcset: brand.logo.webp, sizes }),
    h("img", {
      src: brand.logo.fallback,
      alt: options.alt ?? "Eldrun",
      width: options.width,
      height: logoHeight(options.width),
      class: cx("h-auto", options.class),
      loading: options.priority ? undefined : "lazy",
      fetchpriority: options.priority ? "high" : undefined,
      decoding: options.priority ? undefined : "async",
    }),
  );
}

/** O emblema quadrado (marca do cabeçalho), decorativo ao lado do nome. */
export function emblem(size: 32 | 180 | 512 = 180, cls = "size-8"): HTMLImageElement {
  return h("img", { src: brandUrls().emblem[size], alt: "", width: 32, height: 32, class: cls });
}

export interface StatItem {
  label: string;
  value: Child;
  /** Uma linha menor embaixo do valor (média, total, última vez). */
  detail?: Child | undefined;
  /** O valor vira link (para a lista que ele resume). */
  href?: string | undefined;
  /** Valor em texto corrido (e-mail, data), não um número grande. */
  text?: boolean | undefined;
}

/** Grade de números com rótulo (`<dl>`): o perfil e o resumo do painel usam a mesma. */
export function statGrid(items: readonly StatItem[], columns: 3 | 4 = 4, cls?: string): HTMLDListElement {
  return h(
    "dl",
    { class: cx("stat-grid", columns === 3 ? "stat-grid-3" : "stat-grid-4", cls) },
    ...items.map((item) =>
      h(
        "div",
        { class: "stat" },
        h("dt", { class: "stat-label" }, item.label),
        h("dd", { class: item.text ? "stat-value-text" : "stat-value" }, item.href ? h("a", { href: item.href }, item.value) : item.value, item.detail ? h("small", { class: "stat-detail" }, item.detail) : null),
      ),
    ),
  );
}
